import { zodResolver } from "@hookform/resolvers/zod";
import type { PolarYesNoUndefined } from "@stryker/core/types/section";
import type { FlangeClampDefectDetailsData } from "@stryker/core/types/sections";
import { useForm } from "react-hook-form";
import { z } from "zod";
import PolarQuestion from "@/components/job/PolarQuestion";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep } from "@/constants/inputs";
import { cn } from "@/lib/utils";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";

const FLANGE_CLAMP_TYPES = [
  "center blade",
  "center blade w bars",
  "one sided",
  "one sided w bars",
  "od id w bars",
  "triple seal w tongue",
  "triple seal",
] as const;

const TRIPLE_SEAL_TYPES = ["triple seal w tongue", "triple seal"] as const;
const TRIPLE_SEAL_W_TONGUE_TYPE = "triple seal w tongue";

const FLANGE_CLAMP_TYPE_LABELS: Partial<Record<(typeof FLANGE_CLAMP_TYPES)[number], string>> = {
  "od id w bars": "OD ID w Bars",
};

const numberField = z.union([
  z.number().lt(2147483647),
  z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
]);

const optionalNumberField = z.preprocess(
  (value) => (value === "" || value === null ? undefined : value),
  numberField.optional()
);

const flangeClampDefectDetailsSchema = z
  .object({
    flange_clamp_type: z.string().min(1, { message: "Flange clamp type is required" }),
    thickness_gap: optionalNumberField,
    thickness_gap_calc: optionalNumberField,
    desired_center_groove_size: optionalNumberField,
    drill_flange_to_stud_cavity: z.enum(["yes", "no"]).optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.flange_clamp_type) return;

    if (data.flange_clamp_type === TRIPLE_SEAL_W_TONGUE_TYPE) {
      if (!data.thickness_gap) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Thickness gap is required",
          path: ["thickness_gap"],
        });
      }
      return;
    }

    const isTripleSeal = (TRIPLE_SEAL_TYPES as readonly string[]).includes(data.flange_clamp_type);
    if (isTripleSeal && !data.desired_center_groove_size) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Desired center groove size is required",
        path: ["desired_center_groove_size"],
      });
      return;
    }

    if (!isTripleSeal && !data.thickness_gap) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Thickness gap is required",
        path: ["thickness_gap"],
      });
    }
  });

type FlangeClampDefectDetailsSchemaInput = z.input<typeof flangeClampDefectDetailsSchema>;
type FlangeClampDefectDetailsSchemaOutput = z.output<typeof flangeClampDefectDetailsSchema>;

interface FlangeClampDefectDetailsProps {
  onSave: (data: FlangeClampDefectDetailsData) => void;
}

export function FlangeClampDefectDetails({ onSave }: FlangeClampDefectDetailsProps) {
  const { sections, setSealingDetails } = useCUMechanicalJobStore();
  const savedDefectDetails = sections.defectDetails;
  const saved = savedDefectDetails.data as FlangeClampDefectDetailsData;

  const form = useForm<FlangeClampDefectDetailsSchemaInput, undefined, FlangeClampDefectDetailsSchemaOutput>({
    resolver: zodResolver(flangeClampDefectDetailsSchema),
    defaultValues: {
      flange_clamp_type: saved.flange_clamp_type ?? "",
      thickness_gap:
        saved.thickness_gap ??
        (saved.flange_clamp_type === TRIPLE_SEAL_W_TONGUE_TYPE ? (saved.desired_center_groove_size ?? "") : ""),
      thickness_gap_calc: saved.thickness_gap_calc ?? "",
      desired_center_groove_size: saved.desired_center_groove_size ?? "",
      drill_flange_to_stud_cavity:
        saved.drill_flange_to_stud_cavity === true
          ? "yes"
          : saved.drill_flange_to_stud_cavity === false
            ? "no"
            : undefined,
    },
  });

  const flangeClampType = form.watch("flange_clamp_type");
  const drillFlangeToStudCavity = form.watch("drill_flange_to_stud_cavity") as PolarYesNoUndefined;
  const isTripleSeal = (TRIPLE_SEAL_TYPES as readonly string[]).includes(flangeClampType ?? "");
  const isTripleSealWithTongue = flangeClampType === TRIPLE_SEAL_W_TONGUE_TYPE;
  const shouldUseDesiredCenterGrooveSize = isTripleSeal && !isTripleSealWithTongue;

  function handleDrillFlangeToStudCavity(value: PolarYesNoUndefined) {
    form.setValue("drill_flange_to_stud_cavity", value);
  }

  const handleSave = (payload: FlangeClampDefectDetailsSchemaOutput) => {
    const data: FlangeClampDefectDetailsData = {
      flange_clamp_type: payload.flange_clamp_type,
      ...(shouldUseDesiredCenterGrooveSize
        ? {
            desired_center_groove_size: payload.desired_center_groove_size,
            unit_for_desired_center_groove_size: "Inches",
          }
        : {
            thickness_gap: payload.thickness_gap,
            unit_for_thickness_gap: "Inches",
            thickness_gap_calc: payload.thickness_gap_calc,
          }),
      drill_flange_to_stud_cavity:
        payload.drill_flange_to_stud_cavity === "yes"
          ? true
          : payload.drill_flange_to_stud_cavity === "no"
            ? false
            : null,
      repair_length_options: saved.repair_length_options,
      repair_length_repair_kit_sizes: saved.repair_length_repair_kit_sizes,
    };
    onSave(data);
  };

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSave)} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="flange_clamp_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Flange Clamp Type</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value || undefined}
                      onValueChange={(val) => {
                        field.onChange(val);
                        setSealingDetails({
                          data: {
                            seal_preference: "",
                            seal_type: "",
                            seal_size: "",
                            seal_material: "",
                            sealant: "",
                            sealant_2: "",
                          },
                          status: "open",
                        });
                        // Clear the measurement field when type changes
                        form.setValue("thickness_gap", "");
                        form.setValue("thickness_gap_calc", "");
                        form.setValue("desired_center_groove_size", "");
                      }}
                    >
                      <SelectTrigger className={cn(!field.value && "[&>span]:text-gray-400")}>
                        <SelectValue placeholder="Select flange clamp type" />
                      </SelectTrigger>
                      <SelectContent>
                        {FLANGE_CLAMP_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {FLANGE_CLAMP_TYPE_LABELS[type] ?? type.charAt(0).toUpperCase() + type.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {flangeClampType && (!isTripleSeal || isTripleSealWithTongue) && (
              <FormField
                control={form.control}
                name="thickness_gap"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Thickness Gap</FormLabel>
                    <FormControl>
                      <TextInput
                        type="number"
                        step={inputStep}
                        placeholder="Enter thickness gap"
                        {...field}
                        value={typeof field.value === "string" || typeof field.value === "number" ? field.value : ""}
                        endAdornment={
                          <Typography variant="text-small" className="text-gray-400">
                            in
                          </Typography>
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {flangeClampType && (!isTripleSeal || isTripleSealWithTongue) && (
              <FormField
                control={form.control}
                name="thickness_gap_calc"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Thickness Gap Calc</FormLabel>
                    <FormControl>
                      <TextInput
                        type="number"
                        step={inputStep}
                        placeholder="Enter thickness gap calc"
                        {...field}
                        value={typeof field.value === "string" || typeof field.value === "number" ? field.value : ""}
                        endAdornment={
                          <Typography variant="text-small" className="text-gray-400">
                            in
                          </Typography>
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {flangeClampType && shouldUseDesiredCenterGrooveSize && (
              <FormField
                control={form.control}
                name="desired_center_groove_size"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Desired Center Groove Size</FormLabel>
                    <FormControl>
                      <TextInput
                        type="number"
                        step={inputStep}
                        placeholder="Enter desired center groove size"
                        {...field}
                        value={typeof field.value === "string" || typeof field.value === "number" ? field.value : ""}
                        endAdornment={
                          <Typography variant="text-small" className="text-gray-400">
                            in
                          </Typography>
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          <PolarQuestion
            shouldRender={true}
            question="Drill flange to stud cavity?"
            selection={drillFlangeToStudCavity}
            onYesClick={(e) => {
              e.stopPropagation();
              handleDrillFlangeToStudCavity("yes");
            }}
            onNoClick={(e) => {
              e.stopPropagation();
              handleDrillFlangeToStudCavity("no");
            }}
          />

          <Button type="submit" className="self-end">
            Save
          </Button>
        </form>
      </Form>
    </div>
  );
}
